import React, { useCallback, useEffect, useState } from "react";

import AdminNavBar from "../components/AdminNavBar";
import Toast from "../components/Toast";
import { useConfirmDialog } from "../components/ConfirmDialogProvider";
import {
  AdminEmptyState,
  AdminLoadingState,
  AdminPageBody,
  AdminSectionHeader,
  AdminTablePanel,
  adminButton,
  adminPrimaryButton,
} from "../components/AdminPageShell";
import { useAdminDepartments } from "../store/admin";
import { formatDate } from "./components/invoices/invoiceUtils";

const EMPTY_FORM = { name: "", faculty: "" };

const Departments = () => {
  const confirm = useConfirmDialog();
  const {
    departments,
    departmentsLoading,
    departmentsError,
    departmentMutationLoading,
    fetchDepartments,
    createDepartment,
    updateDepartment,
    deleteDepartment,
  } = useAdminDepartments();

  const [form, setForm] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [toast, setToast] = useState(null);
  const [isSuccess, setIsSuccess] = useState(true);

  const showToast = useCallback((message, success = true) => {
    setToast(message);
    setIsSuccess(success);
    setTimeout(() => setToast(null), 5000);
  }, []);

  useEffect(() => {
    fetchDepartments();
  }, [fetchDepartments]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEdit = (department) => {
    setEditingId(department.id);
    setForm({ name: department.name || "", faculty: department.faculty || "" });
    setShowForm(true);
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    const payload = {
      name: form.name.trim(),
      faculty: form.faculty.trim() || null,
    };
    if (!payload.name) {
      showToast("Department name is required", false);
      return;
    }
    const result = editingId
      ? await updateDepartment(editingId, payload)
      : await createDepartment(payload);
    if (!result.success) {
      showToast(result.error || "Failed to save department", false);
      return;
    }
    showToast(editingId ? "Department updated" : "Department created", true);
    setShowForm(false);
    setEditingId(null);
    setForm(EMPTY_FORM);
    fetchDepartments();
  };

  const handleDelete = (department) =>
    confirm({
      title: `Delete "${department.name}"?`,
      description: "This action cannot be undone.",
      confirmLabel: "Delete department",
      onConfirm: async () => {
        const result = await deleteDepartment(department.id);
        if (!result.success) throw new Error(result.error || "Failed to delete department.");
        showToast("Department deleted", true);
        await fetchDepartments();
      },
    });

  return (
    <div>
      <AdminNavBar title="Departments" />
      <AdminPageBody>
        {toast && <Toast message={toast} onClose={() => setToast(null)} isSuccess={isSuccess} />}

        <AdminSectionHeader
          title="Departments"
          description="Manage the departments used to organise academic resources."
          count={`${departments.length} departments`}
          actions={
            <button className={adminPrimaryButton} onClick={openCreate}>
              Add Department
            </button>
          }
        />

        {showForm && (
          <form
            onSubmit={handleSubmit}
            className="grid gap-4 rounded-lg border border-slate-200 bg-white p-4 shadow-sm md:grid-cols-3"
          >
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Name
              <input
                value={form.name}
                onChange={(event) => setForm({ ...form, name: event.target.value })}
                className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal"
                required
              />
            </label>
            <label className="flex flex-col gap-1 text-xs font-semibold uppercase tracking-wide text-slate-500">
              Faculty
              <input
                value={form.faculty}
                onChange={(event) => setForm({ ...form, faculty: event.target.value })}
                className="h-10 rounded-lg border border-slate-300 px-3 text-sm font-normal normal-case tracking-normal"
              />
            </label>
            <div className="flex items-end gap-2">
              <button type="submit" className={adminPrimaryButton} disabled={departmentMutationLoading}>
                {departmentMutationLoading ? "Saving…" : editingId ? "Save Changes" : "Create"}
              </button>
              <button
                type="button"
                className={adminButton}
                onClick={() => {
                  setShowForm(false);
                  setEditingId(null);
                  setForm(EMPTY_FORM);
                }}
              >
                Cancel
              </button>
            </div>
          </form>
        )}

        {departmentsError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-600">
            {departmentsError}
          </div>
        )}

        {departmentsLoading ? (
          <AdminLoadingState />
        ) : departments.length === 0 ? (
          <AdminEmptyState title="No departments yet" description="Create the first department." />
        ) : (
          <AdminTablePanel>
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead>
                  <tr className="border-b border-slate-200 bg-slate-50">
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">Name</th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">Slug</th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Faculty
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Resources
                    </th>
                    <th className="px-4 py-2 text-xs font-semibold uppercase text-slate-600">
                      Created
                    </th>
                    <th className="px-4 py-2 text-right text-xs font-semibold uppercase text-slate-600">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {departments.map((department) => (
                    <tr key={department.id} className="border-b border-slate-100">
                      <td className="px-4 py-2 text-sm font-medium text-slate-800">
                        {department.name}
                      </td>
                      <td className="px-4 py-2 font-mono text-xs text-slate-500">
                        {department.slug}
                      </td>
                      <td className="px-4 py-2 text-sm text-slate-600">
                        {department.faculty || "—"}
                      </td>
                      <td className="px-4 py-2 text-sm">{department.resourceCount ?? 0}</td>
                      <td className="px-4 py-2 text-xs text-slate-500">
                        {formatDate(department.createdAt)}
                      </td>
                      <td className="px-4 py-2">
                        <div className="flex justify-end gap-3">
                          <button
                            className="text-xs font-semibold text-blue-700 hover:text-blue-900"
                            onClick={() => openEdit(department)}
                          >
                            Edit
                          </button>
                          <button
                            className="text-xs font-semibold text-red-600 hover:text-red-800"
                            onClick={() => handleDelete(department)}
                          >
                            Delete
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </AdminTablePanel>
        )}
      </AdminPageBody>
    </div>
  );
};

export default Departments;
